import { useEffect, useRef, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { CheckCircle2, AlertCircle, Banknote, Search, Building2, ArrowUpCircle, ArrowDownCircle, AlertTriangle, Pause, Play, RotateCcw, Download } from "lucide-react";
import { useNavigate } from "react-router-dom";

const MOCK_CHECKING_BALANCE = 29317.48;

const MOCK_CORP_TRANSACTIONS = [
  {
    id: "corp-txn-1",
    date: "2026-02-14",
    description: "Stripe CONNECT Clearing",
    amount: 8420.0,
    type: "CREDIT",
    detail: "Merchant settlement · batch 0242-FEB14",
  },
  {
    id: "corp-txn-2",
    date: "2026-02-13",
    description: "Corporate Card Interchange Fee Capture",
    amount: 127.33,
    type: "FEE INCOME",
    detail: "Interchange rev · BIN 424242 · 14 txns",
  },
  {
    id: "corp-txn-3",
    date: "2026-02-12",
    description: "Automated Outbound Subcontractor Payroll",
    amount: -3500.0,
    type: "DEBIT",
    detail: "ACH batch · 3 contractors · NET-30 cycle",
  },
];

export default function BankFeed() {
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Corporate ledger — editable local state (backed by /api/v1/bank-feed)
  const [corpTxns, setCorpTxns] = useState(MOCK_CORP_TRANSACTIONS);
  const corpTxnsRef = useRef(MOCK_CORP_TRANSACTIONS);
  const [editingId, setEditingId] = useState(null);
  const [editBuf, setEditBuf] = useState({});
  const [purgeConfirmId, setPurgeConfirmId] = useState(null);

  // Keep ref in sync so the sim interval always picks the latest list
  useEffect(() => { corpTxnsRef.current = corpTxns; }, [corpTxns]);

  // Load persisted rows from the database on mount; fall back to MOCK if unauthenticated
  useEffect(() => {
    api.get("/v1/bank-feed")
      .then((res) => {
        const rows = Array.isArray(res.data) ? res.data : [];
        if (rows.length > 0) {
          setCorpTxns(rows);
          corpTxnsRef.current = rows;
        }
      })
      .catch(() => {});
  }, []);

  const startEdit = (txn) => {
    setEditingId(txn.id);
    setEditBuf({ description: txn.description, amount: txn.amount, type: txn.type, detail: txn.detail || "" });
  };
  const cancelEdit = () => setEditingId(null);
  const saveEdit = (id) => {
    const updated = {
      description: editBuf.description,
      amount: parseFloat(editBuf.amount),
      type: editBuf.type,
      detail: editBuf.detail,
    };
    setCorpTxns((prev) => prev.map((t) => t.id !== id ? t : { ...t, ...updated }));
    setEditingId(null);
    api.patch(`/v1/bank-feed/${id}`, updated).catch(() => {});
  };
  const confirmPurge = (id) => {
    const txn = corpTxns.find((t) => t.id === id);
    if (txn) setLiveBalance((prev) => Math.max(0, Number((prev - txn.amount).toFixed(2))));
    setCorpTxns((prev) => prev.filter((t) => t.id !== id));
    setPurgeConfirmId(null);
    api.delete(`/v1/bank-feed/${id}`).catch(() => {});
  };

  const exportLedgerCSV = () => {
    const cols = ["date", "description", "type", "amount", "detail"];
    const rows = corpTxns.map((t) => cols.map((c) => JSON.stringify(t[c] ?? "")).join(","));
    const blob = new Blob([[cols.join(","), ...rows].join("\n")], { type: "text/csv" });
    const a = Object.assign(document.createElement("a"), {
      href: URL.createObjectURL(blob),
      download: `corp-ledger-${new Date().toISOString().slice(0, 10)}.csv`,
    });
    a.click();
    URL.revokeObjectURL(a.href);
  };

  // --- 30-second checking balance simulation clock ---
  const [liveBalance, setLiveBalance] = useState(MOCK_CHECKING_BALANCE);
  const [simTick, setSimTick] = useState(0);
  const [simCountdown, setSimCountdown] = useState(30);
  const [isPaused, setIsPaused] = useState(false);
  const [simSpeed, setSimSpeed] = useState(1); // 1× / 5× / 10×
  const isPausedRef = useRef(false);

  const togglePause = () => {
    const next = !isPausedRef.current;
    isPausedRef.current = next;
    setIsPaused(next);
    if (!next) {
      const cdMax = Math.max(1, Math.round(30 / simSpeed));
      setSimCountdown(cdMax);
    }
  };

  const resetSim = () => {
    setLiveBalance(MOCK_CHECKING_BALANCE);
    setSimTick(0);
    const cdMax = Math.max(1, Math.round(30 / simSpeed));
    setSimCountdown(cdMax);
    isPausedRef.current = false;
    setIsPaused(false);
  };

  // Recreates intervals whenever simSpeed changes
  useEffect(() => {
    const intervalMs = Math.round(30000 / simSpeed);
    const cdMax = Math.max(1, Math.round(30 / simSpeed));
    setSimCountdown(cdMax);

    // 1s countdown display — freezes when paused
    const cdId = setInterval(() => {
      if (!isPausedRef.current) {
        setSimCountdown((prev) => (prev <= 1 ? cdMax : prev - 1));
      }
    }, 1000);

    // Balance update — skipped entirely when paused; resets countdown after firing
    const simId = setInterval(() => {
      if (isPausedRef.current) return;
      setSimTick((t) => t + 1);
      setSimCountdown(cdMax);
      setLiveBalance((prev) => {
        const pool = corpTxnsRef.current;
        if (!pool.length) return prev;
        const txn = pool[Math.floor(Math.random() * pool.length)];
        const fraction = 0.08 + Math.random() * 0.10;
        const magnitude = Math.abs(txn.amount) * fraction;
        const direction = txn.amount > 0 ? 1 : -1;
        const jitter = Math.random() > 0.5 ? 1 : -1;
        const delta = magnitude * direction * (Math.random() > 0.25 ? 1 : jitter);
        return Math.max(18000, Math.min(48000, Number((prev + delta).toFixed(2))));
      });
    }, intervalMs);

    return () => { clearInterval(cdId); clearInterval(simId); };
  }, [simSpeed]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    api
      .get("/bank/transactions")
      .then((r) => setTxns(r.data || []))
      .finally(() => setLoading(false));
  }, []);

  const matched = txns.filter((t) => t.matched_receipt_id).length;
  const unmatched = txns.length - matched;

  return (
    <DashboardLayout>
      <div className="fade-up">
        <div className="mb-8">
          <div className="text-emerald-400 font-mono text-xs uppercase tracking-[0.3em] mb-2">
            Reconciliation
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tighter text-white">
            Bank feed matching
          </h1>
          <p className="text-slate-400 mt-2 text-sm">
            Real-time card statements cross-referenced with your receipts to catch
            duplicates and missing entries.
          </p>
        </div>

        <div className="grid sm:grid-cols-3 gap-4 mb-8 stagger">
          <div className="glass chroma-strip rounded-2xl p-5">
            <div className="text-slate-400 text-xs uppercase tracking-[0.2em]">
              Transactions
            </div>
            <div className="text-3xl font-mono font-bold text-white mt-2" data-testid="txns-total">
              {txns.length}
            </div>
          </div>
          <div className="glass chroma-strip rounded-2xl p-5">
            <div className="text-slate-400 text-xs uppercase tracking-[0.2em]">
              Matched
            </div>
            <div className="text-3xl font-mono font-bold text-emerald-400 mt-2 chroma-heartbeat-text" data-testid="txns-matched">
              {matched}
            </div>
          </div>
          <div className="glass chroma-strip rounded-2xl p-5">
            <div className="text-slate-400 text-xs uppercase tracking-[0.2em]">
              Missing Receipt
            </div>
            <div className="text-3xl font-mono font-bold text-amber-400 mt-2" data-testid="txns-unmatched">
              {unmatched}
            </div>
          </div>
        </div>

        {/* Receipt Sheets Corporate Checking (Beta) */}
        <div
          className="rounded-2xl mb-8 relative overflow-hidden"
          style={{
            background: "linear-gradient(145deg, rgba(2,14,36,0.96) 0%, rgba(4,8,24,0.98) 100%)",
            border: "1px solid rgba(16,185,129,0.3)",
            boxShadow: "0 0 48px -14px rgba(16,185,129,0.28), inset 0 1px 0 rgba(16,185,129,0.12)",
            backdropFilter: "blur(18px)",
            WebkitBackdropFilter: "blur(18px)",
          }}
          data-testid="corp-checking-card"
        >
          {/* Background glow */}
          <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full pointer-events-none"
            style={{ background: "radial-gradient(circle, rgba(16,185,129,0.18), transparent 65%)", filter: "blur(32px)" }} />
          <div className="absolute -bottom-20 -left-20 w-60 h-60 rounded-full pointer-events-none"
            style={{ background: "radial-gradient(circle, rgba(6,95,70,0.22), transparent 65%)", filter: "blur(24px)" }} />

          {/* Card header */}
          <div className="relative px-6 py-4 border-b" style={{ borderColor: "rgba(16,185,129,0.15)" }}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div
                  className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: "rgba(16,185,129,0.15)", border: "1px solid rgba(16,185,129,0.3)" }}
                >
                  <Building2 size={18} className="text-emerald-400" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-white tracking-tight">
                      Receipt Sheets Corporate Checking
                    </span>
                    <span
                      className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded"
                      style={{ background: "rgba(16,185,129,0.15)", color: "#6ee7b7", border: "1px solid rgba(16,185,129,0.35)" }}
                      data-testid="corp-checking-beta-badge"
                    >
                      BETA
                    </span>
                  </div>
                  <div className="text-[10px] text-slate-400 font-mono mt-0.5">
                    White-label neobank · routing 021000021 · acct ending 7749
                  </div>
                </div>
              </div>

              {/* MOCK warning badge + Sim replay controls */}
              <div className="flex items-center gap-2 flex-wrap justify-end">
                {/* Speed multiplier */}
                <div
                  className="flex items-center gap-0.5 rounded-full overflow-hidden"
                  style={{ border: "1px solid rgba(100,116,139,0.3)", background: "rgba(15,23,42,0.6)" }}
                  data-testid="sim-speed-group"
                >
                  {[1, 5, 10].map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setSimSpeed(s)}
                      data-testid={`sim-speed-${s}x`}
                      className="px-2.5 py-1 text-[9px] font-mono uppercase tracking-widest transition-all"
                      style={
                        simSpeed === s
                          ? { background: "rgba(16,185,129,0.2)", color: "#34d399", borderRadius: "20px" }
                          : { color: "#64748b" }
                      }
                    >
                      {s}×
                    </button>
                  ))}
                </div>
                {/* Pause / Play */}
                <button
                  type="button"
                  onClick={togglePause}
                  data-testid="sim-pause-btn"
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-mono uppercase tracking-widest transition-all hover:brightness-110 active:scale-95"
                  style={
                    isPaused
                      ? { background: "rgba(16,185,129,0.15)", border: "1px solid rgba(16,185,129,0.45)", color: "#6ee7b7" }
                      : { background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.35)", color: "#fca5a5" }
                  }
                >
                  {isPaused ? <Play size={11} /> : <Pause size={11} />}
                  {isPaused ? "RESUME" : "PAUSE"}
                </button>
                {/* Reset */}
                <button
                  type="button"
                  onClick={resetSim}
                  data-testid="sim-reset-btn"
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-mono uppercase tracking-widest transition-all hover:brightness-110 active:scale-95"
                  style={{ background: "rgba(100,116,139,0.12)", border: "1px solid rgba(100,116,139,0.3)", color: "#94a3b8" }}
                >
                  <RotateCcw size={11} />
                  RESET
                </button>
                {/* Export CSV */}
                <button
                  type="button"
                  onClick={exportLedgerCSV}
                  data-testid="export-ledger-csv-btn"
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-mono uppercase tracking-widest transition-all hover:brightness-110 active:scale-95"
                  style={{ background: "rgba(6,182,212,0.1)", border: "1px solid rgba(6,182,212,0.3)", color: "#67e8f9" }}
                >
                  <Download size={11} />
                  Export Statement (CSV)
                </button>
                {/* MOCK badge */}
                <div
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full flex-shrink-0"
                  style={{
                    background: "rgba(245,158,11,0.12)",
                    border: "1px solid rgba(245,158,11,0.45)",
                    boxShadow: "0 0 10px -4px rgba(245,158,11,0.35)",
                  }}
                  data-testid="corp-checking-mock-badge"
                >
                  <AlertTriangle size={11} className="text-amber-400" />
                  <span className="text-[9px] font-mono uppercase tracking-[0.22em] text-amber-300 font-semibold">
                    MOCK SYSTEM — NO LIVE TRANS
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Balance + ledger */}
          <div className="relative px-6 pt-5 pb-6">
            {/* Rolling cash reserve */}
            <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-slate-400 mb-1">
                  Cash Reserve Balance
                </div>
                <div
                  className="text-4xl font-mono font-bold tracking-tight text-white"
                  data-testid="corp-checking-balance"
                >
                  ${liveBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                <div className="text-[10px] font-mono mt-1 flex items-center gap-1" style={{ color: isPaused ? "#f59e0b" : "#34d399" }}>
                  <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: isPaused ? "#f59e0b" : "#34d399" }} />
                  {isPaused
                    ? `PAUSED · Tick #${simTick} · balance frozen`
                    : `${simSpeed > 1 ? `${simSpeed}× SPEED · ` : ""}Sim Tick #${simTick} · next update in ${simCountdown}s`}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div
                  className="px-4 py-2.5 rounded-xl"
                  style={{ background: "rgba(16,185,129,0.08)", border: "1px solid rgba(16,185,129,0.2)" }}
                >
                  <div className="text-[10px] text-slate-500 uppercase tracking-widest font-mono">Inbound MTD</div>
                  <div className="text-base font-mono font-bold text-emerald-300 mt-0.5">+$8,547.33</div>
                </div>
                <div
                  className="px-4 py-2.5 rounded-xl"
                  style={{ background: "rgba(239,68,68,0.07)", border: "1px solid rgba(239,68,68,0.18)" }}
                >
                  <div className="text-[10px] text-slate-500 uppercase tracking-widest font-mono">Outbound MTD</div>
                  <div className="text-base font-mono font-bold text-red-300 mt-0.5">-$3,500.00</div>
                </div>
              </div>
            </div>

            {/* Transaction ledger */}
            <div
              className="rounded-xl overflow-hidden"
              style={{ border: "1px solid rgba(16,185,129,0.12)" }}
              data-testid="corp-checking-ledger"
            >
              {/* Ledger header */}
              <div
                className="grid grid-cols-12 px-4 py-2.5 text-[9px] font-mono uppercase tracking-widest text-slate-500"
                style={{ background: "rgba(16,185,129,0.06)", borderBottom: "1px solid rgba(16,185,129,0.1)" }}
              >
                <div className="col-span-2">Date</div>
                <div className="col-span-4">Description</div>
                <div className="col-span-2">Type</div>
                <div className="col-span-2 text-right">Amount</div>
                <div className="col-span-2 text-right">Actions</div>
              </div>

              {/* Ledger rows */}
              <div className="divide-y" style={{ borderColor: "rgba(255,255,255,0.04)" }}>
                {corpTxns.length === 0 && (
                  <div className="px-4 py-12 text-center">
                    <div className="text-[10px] font-mono text-emerald-400/70 uppercase tracking-[0.25em]">LEDGER INITIALIZED · CASH RESERVE ${MOCK_CHECKING_BALANCE.toLocaleString("en-US", { minimumFractionDigits: 2 })}</div>
                    <div className="mt-1.5 text-[9px] font-mono text-slate-600 uppercase tracking-widest">Awaiting first corporate transaction entry</div>
                  </div>
                )}
                {corpTxns.map((txn) => {
                  const isCredit = txn.amount > 0;
                  const isFee = txn.type === "FEE INCOME";
                  const isEditing = editingId === txn.id;
                  const isPurging = purgeConfirmId === txn.id;
                  return (
                    <div
                      key={txn.id}
                      className="grid grid-cols-12 px-4 py-3.5 items-center hover:bg-white/[0.02] transition-colors"
                      data-testid={`corp-txn-${txn.id}`}
                    >
                      {isPurging ? (
                        <>
                          <div className="col-span-6 text-xs font-mono text-rose-300 flex items-center gap-2">
                            Delete &quot;{txn.description}&quot;?
                          </div>
                          <div className="col-span-6 flex items-center justify-end gap-2">
                            <button
                              onClick={() => confirmPurge(txn.id)}
                              data-testid={`confirm-purge-${txn.id}`}
                              className="px-2.5 py-1 rounded text-[9px] font-mono uppercase tracking-widest transition-all hover:scale-105"
                              style={{ background: "rgba(239,68,68,0.15)", border: "1px solid rgba(239,68,68,0.4)", color: "#f87171" }}
                            >Delete</button>
                            <button
                              onClick={() => setPurgeConfirmId(null)}
                              className="px-2.5 py-1 rounded text-[9px] font-mono uppercase tracking-widest"
                              style={{ background: "rgba(100,116,139,0.1)", border: "1px solid rgba(100,116,139,0.22)", color: "#94a3b8" }}
                            >Cancel</button>
                          </div>
                        </>
                      ) : isEditing ? (
                        <>
                          {/* Date unchanged */}
                          <div className="col-span-2 text-[11px] font-mono text-slate-400">{txn.date}</div>
                          {/* Description input */}
                          <div className="col-span-4 pr-2">
                            <input
                              type="text"
                              value={editBuf.description}
                              onChange={(e) => setEditBuf((b) => ({ ...b, description: e.target.value }))}
                              onKeyDown={(e) => { if (e.key === "Enter") saveEdit(txn.id); if (e.key === "Escape") cancelEdit(); }}
                              data-testid={`edit-desc-${txn.id}`}
                              className="w-full rounded px-2 py-1 text-xs font-mono text-white outline-none"
                              style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(6,182,212,0.35)" }}
                            />
                          </div>
                          {/* Type dropdown */}
                          <div className="col-span-2 pr-1">
                            <select
                              value={editBuf.type}
                              onChange={(e) => setEditBuf((b) => ({ ...b, type: e.target.value }))}
                              data-testid={`edit-type-${txn.id}`}
                              className="w-full rounded px-1 py-1 text-[9px] font-mono uppercase outline-none cursor-pointer"
                              style={{ background: "rgba(15,23,42,0.9)", border: "1px solid rgba(255,255,255,0.1)", color: "#94a3b8" }}
                            >
                              <option value="CREDIT">CREDIT</option>
                              <option value="DEBIT">DEBIT</option>
                              <option value="FEE INCOME">FEE INCOME</option>
                            </select>
                          </div>
                          {/* Amount input */}
                          <div className="col-span-2 pl-1">
                            <input
                              type="number" step="0.01"
                              value={editBuf.amount}
                              onChange={(e) => setEditBuf((b) => ({ ...b, amount: parseFloat(e.target.value) || 0 }))}
                              data-testid={`edit-amount-${txn.id}`}
                              className="w-full text-right rounded px-2 py-1 text-xs font-mono outline-none"
                              style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(6,182,212,0.35)", color: "#e2e8f0", MozAppearance: "textfield" }}
                            />
                          </div>
                          {/* Save / cancel */}
                          <div className="col-span-2 flex items-center justify-end gap-1.5">
                            <button
                              onClick={() => saveEdit(txn.id)}
                              data-testid={`save-txn-${txn.id}`}
                              className="px-2 py-1 rounded text-[9px] font-mono transition-all hover:scale-105"
                              style={{ background: "rgba(16,185,129,0.15)", border: "1px solid rgba(16,185,129,0.4)", color: "#34d399" }}
                            >💾</button>
                            <button
                              onClick={cancelEdit}
                              className="px-2 py-1 rounded text-[9px] font-mono"
                              style={{ background: "rgba(100,116,139,0.1)", border: "1px solid rgba(100,116,139,0.22)", color: "#94a3b8" }}
                            >✕</button>
                          </div>
                        </>
                      ) : (
                        <>
                          {/* Date */}
                          <div className="col-span-2 text-[11px] font-mono text-slate-400">{txn.date}</div>
                          {/* Description + detail */}
                          <div className="col-span-4">
                            <div className="flex items-center gap-2">
                              <div
                                className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0"
                                style={{ background: isCredit ? "rgba(16,185,129,0.12)" : "rgba(239,68,68,0.1)" }}
                              >
                                {isCredit ? <ArrowUpCircle size={12} className="text-emerald-400" /> : <ArrowDownCircle size={12} className="text-red-400" />}
                              </div>
                              <div className="min-w-0">
                                <div className="text-xs font-medium text-white leading-tight truncate">{txn.description}</div>
                                <div className="text-[10px] text-slate-500 font-mono leading-tight mt-0.5 truncate">{txn.detail}</div>
                              </div>
                            </div>
                          </div>
                          {/* Type badge */}
                          <div className="col-span-2">
                            <span
                              className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded whitespace-nowrap"
                              style={
                                isFee
                                  ? { background: "rgba(139,92,246,0.15)", color: "#c4b5fd", border: "1px solid rgba(139,92,246,0.3)" }
                                  : isCredit
                                  ? { background: "rgba(16,185,129,0.12)", color: "#6ee7b7", border: "1px solid rgba(16,185,129,0.3)" }
                                  : { background: "rgba(239,68,68,0.1)", color: "#fca5a5", border: "1px solid rgba(239,68,68,0.28)" }
                              }
                            >{txn.type}</span>
                          </div>
                          {/* Amount */}
                          <div className="col-span-2 text-right">
                            <div
                              className="font-mono font-bold text-sm"
                              style={{ color: isCredit ? "#34d399" : "#f87171" }}
                              data-testid={`corp-txn-amount-${txn.id}`}
                            >
                              {isCredit ? "+" : "-"}${Math.abs(txn.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </div>
                          </div>
                          {/* Edit / Delete */}
                          <div className="col-span-2 flex items-center justify-end gap-1">
                            <button
                              onClick={() => startEdit(txn)}
                              data-testid={`edit-txn-${txn.id}`}
                              title="Edit entry"
                              className="p-1.5 rounded-lg transition-all hover:scale-110 active:scale-95"
                              style={{ background: "rgba(6,182,212,0.08)", border: "1px solid rgba(6,182,212,0.22)", color: "#67e8f9" }}
                            >✏️</button>
                            <button
                              onClick={() => setPurgeConfirmId(txn.id)}
                              data-testid={`purge-txn-${txn.id}`}
                              title="Delete entry"
                              className="p-1.5 rounded-lg transition-all hover:scale-110 active:scale-95"
                              style={{ background: "rgba(239,68,68,0.07)", border: "1px solid rgba(239,68,68,0.2)", color: "#f87171" }}
                            >❌</button>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Footer note */}
            <div className="mt-3 text-[10px] text-slate-600 font-mono text-center">
              Sandbox ledger · transactions are simulated · White-Label Corporate Checking powered by Receipt Sheets Capital (Beta)
            </div>
          </div>
        </div>

        {/* Existing bank feed table */}
        <div className="glass chroma-strip rounded-2xl overflow-hidden">
          <div className="px-6 py-4 border-b border-white/5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Banknote size={18} className="text-emerald-400" />
              <span className="text-sm font-semibold uppercase tracking-widest text-slate-300">
                Live Feed · Card ending 4242
              </span>
            </div>
            <span className="text-xs font-mono text-emerald-400 uppercase tracking-widest">
              ● Streaming
            </span>
          </div>
          {loading ? (
            <div className="text-center py-16 text-slate-500 font-mono text-sm tracking-widest">
              LOADING FEED…
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {txns.map((t) => (
                <div
                  key={t.id}
                  className={`px-6 py-4 flex items-center gap-4 chroma-strip rounded-xl mx-2 my-1 ${t.matched_receipt_id ? "chroma-heartbeat" : ""}`}
                  data-testid={`bank-txn-${t.id}`}
                >
                  <div
                    className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                      t.matched_receipt_id
                        ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/25"
                        : "bg-amber-500/10 text-amber-400 border border-amber-500/25"
                    }`}
                  >
                    {t.matched_receipt_id ? (
                      <CheckCircle2 size={18} />
                    ) : (
                      <AlertCircle size={18} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-white truncate">{t.description}</div>
                    <div className="text-xs text-slate-500 font-mono mt-0.5">{t.date}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono font-bold text-white">
                      ${Number(t.amount).toFixed(2)}
                    </div>
                    {t.matched_receipt_id ? (
                      <button
                        onClick={() => navigate(`/receipts/${t.matched_receipt_id}`)}
                        className="text-[10px] uppercase tracking-widest text-emerald-400 hover:text-emerald-300 font-mono mt-0.5"
                      >
                        View receipt →
                      </button>
                    ) : (
                      <div className="text-[10px] uppercase tracking-widest text-amber-400 font-mono mt-0.5">
                        Missing receipt
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
